import { useGetInstance } from "../../Hooks/use-get";
import { useMutate } from "../../Hooks/use-mutate";
import { useParams } from "react-router-dom";

import { PageLayout } from "../../Components/PageLayout";
import { Card } from "../../Components/ui/card";

import { Form } from "../../Components/ui/form/form";
import { handleFieldChange } from "../../Components/ui/form/utility/field-change";
import { useEffect, useState } from "react";
import { Input, Select } from "../../Components/ui/form";
import { SearchSelect } from "../../Components/ui/form/search-select/search-select";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";
import { Button } from "../shared";

type UserInstance = {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  location: {
    id: number;
    loc_name: string;
  };
  groups: Array<{
    id: number;
    name: string;
  }>;
};

type UserFormData = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  isActive: string;
  location: {
    label: string;
    value: string | number;
  } | null;
  groupId: number;
};

type UserSubmitData = {
  first_name: string;
  last_name: string;
  username: string;
  email: string;
  is_active: boolean;
  location_id: number;
  group_id: number;
};

const searchURL = "/assets/locations/v2/?is_active=true&name=";

const UserRoute = () => {
  const { id } = useParams<{ id: string }>();

  const user = useGetInstance<UserInstance>("/api/users/" + id + "/");
  const groups =
    useGetInstance<Array<{ id: number; name: string }>>("/api/groups/");

  const [formData, setFormData] = useState<UserFormData>({
    firstName: "",
    lastName: "",
    username: "",
    email: "",
    isActive: "",
    location: null,
    groupId: 0,
  });

  const updateUser = useMutate<UserSubmitData>("/api/users/" + id + "/", "PATCH");

  useEffect(() => {
    if (user.data) {
      setFormData({
        firstName: user.data.first_name,
        lastName: user.data.last_name,
        username: user.data.username,
        email: user.data.email,
        isActive: user.data.is_active === true ? "true" : "false",
        location: {
          label: user.data.location.loc_name,
          value: user.data.location.id,
        },
        groupId: user.data.groups.length > 0 ? user.data.groups[0].id : 0,
      });
    }
  }, [user.data]);

  const handleSubmit = (event: React.SubmitEvent) => {
    event.preventDefault();
    const submitData: UserSubmitData = {
      first_name: formData.firstName,
      last_name: formData.lastName,
      username: formData.username,
      email: formData.email,
      is_active: formData.isActive === "true",
      location_id: formData.location?.value as number,
      group_id: formData.groupId,
    };
    updateUser.mutate(submitData);

  };

  if (user.error) {
    return (
      <PageLayout title="" header="">
        <></>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="" header="">
      <Button
        variant="secondary"
        onClick={() => window.history.back()}
        labelKey="back"
      />
      <Card
        size="md"
        title={`${formData.firstName + " " + formData.lastName}`}
        titleKey=""
      >
        <Form onSubmit={handleSubmit} isLoading={updateUser.isLoading} submitLabel="save">
          <Input
            name="firstName"
            label="firstName"
            value={formData.firstName}
            onChange={(e) => handleFieldChange(e, setFormData)}
          />
          <Input
            name="lastName"
            label="lastName"
            value={formData.lastName}
            onChange={(e) => handleFieldChange(e, setFormData)}
          />
          <Input
            name="username"
            label="username"
            value={formData.username}
            onChange={(e) => handleFieldChange(e, setFormData)}
          />
          <Input
            name="email"
            label="email"
            value={formData.email}
            onChange={(e) => handleFieldChange(e, setFormData)}
          />
          <Select
            name="isActive"
            label="status"
            value={formData.isActive}
            onChange={(e) => handleFieldChange(e, setFormData)}
            options={[
              { optionValue: "true", optionLabel: "active" },
              { optionValue: "false", optionLabel: "inactive" },
            ]}
          />
          <SearchSelect
            value={formData.location}
            onChange={(selected) => {
              setFormData((prev) => ({
                ...prev,
                location: selected,
              }));
            }}
            label="location"
            url={searchURL}
            accessor={{
              optionLabel: (item) => (item as any).loc_name,
              optionValue: (item) => (item as any).id,
            }}
          />
          {groups.isLoading ? (
            <Spinner />
          ) : (
            <Select
              name="groupId"
              label="role"
              value={formData.groupId}
              onChange={(e) => handleFieldChange(e, setFormData)}
              options={
                groups.data?.map((group) => ({
                  optionValue: group.id,
                  optionLabel: group.name,
                })) || []
              }
            />
          )}
        </Form>
      </Card>
    </PageLayout>
  );
};

export default UserRoute;
